import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, GeoJSON, Polyline, ImageOverlay, useMap } from "react-leaflet";
import L from "leaflet";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ModuleNav } from "@/components/symin/ModuleNav";
import { AreaSelect } from "@/components/symin/AreaSelect";
import { Button } from "@/components/ui/button";
import { StackSimple, ArrowSquareOut, Stack } from "@phosphor-icons/react";

const CLASS_COLOR = { Fault: "#F43F5E", Fracture: "#38BDF8", Lineament: "#94A3B8", Shear: "#E67332" };
const classColor = (c) => CLASS_COLOR[c] || "#94A3B8";
const toLL = (coords) => (coords || []).map(([x, y]) => [y, x]);

function FitBounds({ fc }) {
  const map = useMap();
  useEffect(() => {
    try { const b = L.geoJSON(fc).getBounds(); if (b.isValid()) map.fitBounds(b, { padding: [30, 30] }); } catch (e) { /* */ }
  }, [fc, map]);
  useEffect(() => { const t = setTimeout(() => map.invalidateSize({ animate: false }), 80); return () => clearTimeout(t); }, [map]);
  return null;
}

function RoseDiagram({ bins }) {
  if (!bins || !bins.length) return null;
  const max = Math.max(...bins, 1), cx = 70, cy = 70, R = 58;
  const path = (i, val, off) => {
    const r = Math.max(2, (val / max) * R);
    const a0 = ((i * 10 + off) * Math.PI) / 180, a1 = (((i + 1) * 10 + off) * Math.PI) / 180;
    const p0 = [cx + r * Math.sin(a0), cy - r * Math.cos(a0)];
    const p1 = [cx + r * Math.sin(a1), cy - r * Math.cos(a1)];
    return `M${cx},${cy} L${p0[0].toFixed(1)},${p0[1].toFixed(1)} A${r.toFixed(1)},${r.toFixed(1)} 0 0 1 ${p1[0].toFixed(1)},${p1[1].toFixed(1)} Z`;
  };
  return (
    <svg viewBox="0 0 140 140" className="w-full max-w-[180px] mx-auto" data-testid="geology-rose">
      {[R, (R * 2) / 3, R / 3].map((r) => <circle key={r} cx={cx} cy={cy} r={r} fill="none" stroke="#334155" strokeOpacity="0.4" strokeWidth="0.6" />)}
      <line x1={cx} y1={cy - R} x2={cx} y2={cy + R} stroke="#334155" strokeOpacity="0.4" strokeWidth="0.6" />
      <line x1={cx - R} y1={cy} x2={cx + R} y2={cy} stroke="#334155" strokeOpacity="0.4" strokeWidth="0.6" />
      {bins.map((v, i) => <path key={`a${i}`} d={path(i, v, 0)} fill="#C20511" fillOpacity="0.8" stroke="#0B1420" strokeWidth="0.4" />)}
      {bins.map((v, i) => <path key={`b${i}`} d={path(i, v, 180)} fill="#C20511" fillOpacity="0.8" stroke="#0B1420" strokeWidth="0.4" />)}
      <text x={cx} y="10" textAnchor="middle" fontSize="8" fill="#94A3B8">N</text>
    </svg>
  );
}

export default function GeologyStructure() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [area, setArea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState({ Fault: true, Fracture: true, Lineament: true, Shear: true, density: false });

  const fetchArea = (aid) => {
    setLoading(true);
    api.get(`/projects/${id}/geology-structure${aid ? `?aid=${aid}` : ""}`)
      .then((r) => { setData(r.data); setArea(r.data.area_id); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchArea(null); }, [id]);

  const feats = data?.features || [];
  const boundaryFC = useMemo(() => data?.boundary ? { type: "Feature", geometry: data.boundary } : null, [data]);
  const bbox = data?.bbox;
  const imgBounds = bbox ? [[bbox[1], bbox[0]], [bbox[3], bbox[2]]] : null;
  const classes = data?.classes || {};

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ModuleNav pid={id} active="geology" />
      <div className="ml-[64px] flex flex-col h-screen">
        <header className="h-[52px] shrink-0 bg-card/90 backdrop-blur-md border-b border-border flex items-center gap-2 sm:gap-3 px-3 sm:px-5">
          <StackSimple size={18} style={{ color: "#10B981" }} className="shrink-0" />
          <h1 className="font-display font-semibold text-sm sm:text-lg tracking-tight truncate min-w-0" data-testid="geology-title">Geology &amp; Structure</h1>
          <span className="text-xs text-muted-foreground truncate hidden lg:block">{data?.project_name}</span>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <AreaSelect areas={data?.areas || []} value={area} onChange={fetchArea} accent="#10B981" testid="geology-area-select" />
            <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => nav(`/projects/${id}/command`)} data-testid="geology-back">
              <ArrowSquareOut size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">Command Centre</span>
            </Button>
          </div>
        </header>

        <div className="flex-1 flex min-h-0 flex-col lg:flex-row">
          <aside className="w-full lg:w-72 shrink-0 border-b lg:border-b-0 lg:border-r border-border bg-card overflow-y-auto max-h-[36vh] lg:max-h-none" data-testid="geology-panel">
            <div className="px-3 py-3 border-b border-border">
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1.5"><Stack size={14} /> Structural Layers</div>
              {["Fault", "Fracture", "Shear", "Lineament"].map((c) => (classes[c] ? (
                <button key={c} data-testid={`geology-layer-${c.toLowerCase()}`} onClick={() => setShow((s) => ({ ...s, [c]: !s[c] }))}
                  className="flex items-center gap-2 w-full px-1 py-1.5 rounded-md text-xs hover:bg-accent/50 transition-colors text-left">
                  <span className="h-3 w-3 rounded-[3px] shrink-0" style={{ background: show[c] ? classColor(c) : "transparent", border: `1px solid ${classColor(c)}` }} />
                  <span className={show[c] ? "text-foreground" : "text-muted-foreground"}>{c}s</span>
                  <span className="ml-auto font-mono text-[10px] text-muted-foreground">{classes[c]}</span>
                </button>
              ) : null))}
              {data?.density_png && (
                <button data-testid="geology-layer-density" onClick={() => setShow((s) => ({ ...s, density: !s.density }))}
                  className="flex items-center gap-2 w-full px-1 py-1.5 rounded-md text-xs hover:bg-accent/50 transition-colors text-left mt-1">
                  <span className="h-3 w-3 rounded-[3px] shrink-0" style={{ background: show.density ? "#E67332" : "transparent", border: "1px solid #E67332" }} />
                  <span className={show.density ? "text-foreground" : "text-muted-foreground"}>Lineament density</span>
                </button>
              )}
            </div>
            <div className="px-3 py-3 border-b border-border">
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-2">Orientation Rose</div>
              <RoseDiagram bins={data?.rose_bins} />
            </div>
            <div className="px-3 py-3">
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">Lineaments</span>
                <span className="font-mono font-bold text-lg" style={{ color: "#10B981" }} data-testid="geology-count">{data?.count || 0}</span>
              </div>
              {data?.favourability_note && <p className="text-xs text-muted-foreground leading-relaxed mt-1">{data.favourability_note}</p>}
            </div>
          </aside>

          <div className="relative flex-1 min-w-0 bg-[#04070B]">
            {loading ? (
              <div className="absolute inset-0 grid place-items-center text-muted-foreground text-sm">Loading structural data…</div>
            ) : (
              <div className="absolute inset-0" data-testid="geology-map">
                <MapContainer style={{ height: "100%", width: "100%", background: "#04070B" }} center={[0, 20]} zoom={5} attributionControl={true}>
                  <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}" attribution="© Esri" maxZoom={19} />
                  {show.density && data?.density_png && imgBounds && <ImageOverlay url={data.density_png} bounds={imgBounds} opacity={0.55} />}
                  {boundaryFC && <GeoJSON key={area} data={boundaryFC} style={{ color: "#C20511", weight: 2, fill: false }} />}
                  {feats.map((f, i) => {
                    const cl = (f.properties || {}).class || "Lineament";
                    if (!show[cl]) return null;
                    const g = f.geometry || {};
                    if (g.type !== "LineString") return null;
                    return <Polyline key={i} positions={toLL(g.coordinates)} pathOptions={{ color: classColor(cl), weight: cl === "Fault" ? 2.5 : 1.3, opacity: 0.9 }} />;
                  })}
                  {boundaryFC && <FitBounds fc={boundaryFC} />}
                </MapContainer>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
